import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Flame, Loader2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chatbot conversation flow
const buildSteps = (productContext, productOptions) => [
  {
    id: 'greet',
    bot: `Buenas tchê! 🤠 Sou o assistente da Rotativa Gaúcha.${productContext ? ` Vi que tu te interessou pelo modelo "${productContext}".` : ''} Vou te fazer umas perguntas rapidinhas para mandar começarmos a proza.`,
    type: 'continue',
    next: 'name'
  },
  {
    id: 'name',
    bot: 'Beleza! Primeiro, como tu te chamas? 😊',
    type: 'text',
    placeholder: 'Teu nome',
    field: 'name',
    validate: (v) => v.trim().length >= 2 || 'Me diz teu nome (mínimo 2 letras)',
    next: 'phone'
  },
  {
    id: 'phone',
    bot: (data) => `Show, ${data.name.split(' ')[0]}! Qual teu WhatsApp pra nosso vendedor te chamar? (com DDD)`,
    type: 'phone',
    placeholder: '(55) 99999-9999',
    field: 'phone',
    validate: (v) => v.replace(/\D/g, '').length >= 10 || 'Telefone inválido. Coloca o DDD + número',
    next: 'city'
  },
  {
    id: 'city',
    bot: 'De qual cidade tu és? (precisamos pro frete)',
    type: 'text',
    placeholder: 'Ex: Porto Alegre',
    field: 'city',
    validate: (v) => v.trim().length >= 2 || 'Me diz a cidade',
    next: 'state'
  },
  {
    id: 'state',
    bot: 'E o estado?',
    type: 'buttons',
    field: 'state',
    options: ['RS', 'SC', 'PR', 'SP', 'MG', 'RJ', 'Outro'],
    next: 'productInterest'
  },
  {
    id: 'productInterest',
    bot: 'Qual modelo de rotativa te interessa mais?',
    type: 'buttons',
    field: 'productInterest',
    options: productOptions && productOptions.length > 0
      ? [...productOptions, 'Não sei, me ajuda decidir']
      : ['Não sei, me ajuda decidir'],
    next: 'size'
  },
  {
    id: 'size',
    bot: 'Que tamanho tu pensas? (depende de quantas pessoas vai atender)',
    type: 'buttons',
    field: 'size',
    options: ['Pequena', 'Grande', 'Não sei ainda'],
    next: 'usageType'
  },
  {
    id: 'usageType',
    bot: 'Vai ser pra uso pessoal ou comercial?',
    type: 'buttons',
    field: 'usageType',
    options: ['Pessoal e Família', 'Comercial e Restaurante', 'Eventos e Festas'],
    next: 'timeline'
  },
  {
    id: 'timeline',
    bot: 'E pra quando tu pretendes comprar?',
    type: 'buttons',
    field: 'timeline',
    options: ['Quero agora!', 'Próximos 30 dias', 'Em até 3 meses', 'Só pesquisando'],
    next: 'paymentPreference'
  },
  {
    id: 'paymentPreference',
    bot: 'Última pergunta! Forma de pagamento preferida?',
    type: 'buttons',
    field: 'paymentPreference',
    options: ['À vista', 'Pix', 'Boleto', 'A combinar'],
    next: 'final'
  },
  {
    id: 'final',
    bot: (data) => `Mass que Bagual, ${data.name.split(' ')[0]}! 🔥\n\nJá registrei tuas informações e não te atucana que o nosso comercial já vai te chamar de Vereda.`,
    type: 'finish'
  }
];

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const buildWhatsAppMessage = (data) => {
  const lines = [
    'Olá! Vim pelo site da Rotativa Gaúcha e me qualifiquei pelo assistente. Segue meus dados:',
    '',
    `*Nome:* ${data.name}`,
    `*Telefone:* ${data.phone}`,
    `*Cidade:* ${data.city}/${data.state}`,
    `*Modelo de interesse:* ${data.productInterest}`,
    `*Tamanho:* ${data.size}`,
    `*Tipo de uso:* ${data.usageType}`,
    `*Prazo de compra:* ${data.timeline}`,
    `*Pagamento:* ${data.paymentPreference}`,
    '',
    'Aguardo retorno!'
  ];
  return lines.join('\n');
};

const QualifyChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [productContext, setProductContext] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [steps, setSteps] = useState(() => buildSteps('', []));
  const [currentStepId, setCurrentStepId] = useState('greet');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [data, setData] = useState({});
  const [error, setError] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [viewportTop, setViewportTop] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const currentStep = steps.find(s => s.id === currentStepId);

  // Fetch products once on mount so the chatbot's "modelo" question reflects the catalog.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (cancelled) return;
        const names = (res.data || [])
          .filter((p) => p?.name && !/envio|frete/i.test(p.name))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((p) => p.name);
        setProductOptions(names);
      })
      .catch(() => {
        // Silent fallback: chatbot still works without dynamic options
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  // Track the visual viewport (handles mobile keyboard show/hide smoothly)
  useEffect(() => {
    if (!isOpen) return;
    const updateHeight = () => {
      const vv = window.visualViewport;
      const h = vv?.height || window.innerHeight;
      const t = vv?.offsetTop || 0;
      setViewportHeight(h);
      setViewportTop(t);
      // Keep latest message visible when keyboard opens
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
      });
    };
    updateHeight();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', updateHeight);
      vv.addEventListener('scroll', updateHeight);
    }
    window.addEventListener('resize', updateHeight);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateHeight);
        vv.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, [isOpen]);

  // Lock body scroll when chatbot is open (prevents page-behind scroll on mobile)
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Reset chat when opening fresh
  const startConversation = useCallback((context = '') => {
    const newSteps = buildSteps(context, productOptions);
    setSteps(newSteps);
    setProductContext(context);
    setData({});
    setMessages([]);
    setCurrentStepId('greet');
    setInputValue('');
    setError('');
    setSubmitted(false);
    setSubmitting(false);

    // Add greeting bot message
    setIsTyping(true);
    setTimeout(() => {
      const greet = newSteps[0];
      const text = typeof greet.bot === 'function' ? greet.bot({}) : greet.bot;
      setMessages([{ from: 'bot', text }]);
      setIsTyping(false);
      scrollToBottom();
    }, 600);
  }, [scrollToBottom, productOptions]);

  // Listen for global open events from CTAs
  useEffect(() => {
    const handler = (e) => {
      const ctx = e?.detail?.context || '';
      setIsOpen(true);
      startConversation(ctx);
    };
    window.addEventListener('open-chatbot', handler);
    return () => window.removeEventListener('open-chatbot', handler);
  }, [startConversation]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Focus input when step changes to text/phone
  useEffect(() => {
    if (isOpen && (currentStep?.type === 'text' || currentStep?.type === 'phone')) {
      setTimeout(() => {
        inputRef.current?.focus();
        // Scroll latest message into view after keyboard opens
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ block: 'end' });
        }, 350);
      }, 200);
    }
  }, [currentStepId, isOpen, currentStep]);

  const saveLead = useCallback(async (finalData) => {
    if (submitted || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...finalData,
        source: productContext ? `produto: ${productContext}` : 'site'
      };
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to save lead', err);
      // Mark as submitted anyway so the WhatsApp button still works smoothly
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }, [submitted, submitting, productContext]);

  const advanceStep = (nextId, newData) => {
    setError('');
    setInputValue('');
    setIsTyping(true);
    scrollToBottom();
    setTimeout(() => {
      const next = steps.find(s => s.id === nextId);
      if (next) {
        const text = typeof next.bot === 'function' ? next.bot(newData) : next.bot;
        setMessages(prev => [...prev, { from: 'bot', text }]);
        setCurrentStepId(nextId);
        // Auto-save lead the moment we reach the final step.
        // The CRM and notifications fire even if the user closes the modal here.
        if (next.type === 'finish') {
          saveLead(newData);
        }
      }
      setIsTyping(false);
      scrollToBottom();
    }, 700);
  };

  const handleTextSubmit = (e) => {
    e?.preventDefault();
    if (!currentStep || (currentStep.type !== 'text' && currentStep.type !== 'phone')) return;

    const value = inputValue.trim();
    const validation = currentStep.validate ? currentStep.validate(value) : true;
    if (validation !== true) {
      setError(typeof validation === 'string' ? validation : 'Valor inválido');
      return;
    }

    const newData = { ...data, [currentStep.field]: value };
    setData(newData);
    setMessages(prev => [...prev, { from: 'user', text: value }]);
    advanceStep(currentStep.next, newData);
  };

  const handleButtonClick = (option) => {
    if (!currentStep) return;
    if (currentStep.type === 'continue') {
      setMessages(prev => [...prev, { from: 'user', text: 'Bora!' }]);
      advanceStep(currentStep.next, data);
      return;
    }
    if (currentStep.type === 'buttons') {
      const newData = { ...data, [currentStep.field]: option };
      setData(newData);
      setMessages(prev => [...prev, { from: 'user', text: option }]);
      advanceStep(currentStep.next, newData);
    }
  };

  // Lead is auto-saved when conversation reaches the final step.
  // The WhatsApp button is now an <a href> link, so no onClick handler needed.

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating launcher button (always visible when chat closed) */}
      {!isOpen && (
        <button
          data-testid="chatbot-launcher-btn"
          onClick={() => { setIsOpen(true); startConversation(''); }}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-amber-500 to-orange-600 hover:scale-105 text-stone-900 rounded-full p-4 shadow-2xl shadow-amber-500/30 transition-all duration-200 group"
          aria-label="Abrir chat de atendimento"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold animate-pulse">1</span>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-stone-900 text-white text-sm whitespace-nowrap px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
            Falar com vendedor
          </span>
        </button>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <div
          data-testid="chatbot-modal"
          className="fixed left-0 right-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn overflow-hidden"
          onClick={handleClose}
          style={{
            top: `${viewportTop}px`,
            height: `${viewportHeight}px`,
          }}
        >
          <div
            className="bg-stone-900 w-full sm:max-w-md sm:rounded-2xl shadow-2xl border-t-2 sm:border-2 border-amber-500/40 flex flex-col sm:h-[640px] animate-slideUp overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ height: `${viewportHeight}px`, maxHeight: `${viewportHeight}px` }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-700 bg-gradient-to-r from-amber-600/20 to-orange-600/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-stone-900" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Rotativa Gaúcha</h3>
                  <p className="text-amber-400 text-xs flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Online agora
                  </p>
                </div>
              </div>
              <button
                data-testid="chatbot-close-btn"
                onClick={handleClose}
                className="text-stone-400 hover:text-white p-1 rounded-full hover:bg-stone-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div data-testid="chatbot-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-900">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}>
                  {msg.from === 'bot' && (
                    <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center mr-2 mt-auto flex-shrink-0">
                      <Flame className="w-3.5 h-3.5 text-stone-900" />
                    </div>
                  )}
                  <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${
                    msg.from === 'user'
                      ? 'bg-amber-500 text-stone-900 rounded-br-sm font-medium'
                      : 'bg-stone-800 text-stone-100 rounded-bl-sm border border-stone-700'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start animate-fadeIn">
                  <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center mr-2 mt-auto flex-shrink-0">
                    <Flame className="w-3.5 h-3.5 text-stone-900" />
                  </div>
                  <div className="bg-stone-800 border border-stone-700 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            {!isTyping && currentStep && (
              <div className="border-t border-stone-700 bg-stone-800/50 p-3">
                {error && (
                  <p data-testid="chatbot-error" className="text-red-400 text-xs mb-2 px-1">{error}</p>
                )}

                {currentStep.type === 'continue' && (
                  <button
                    data-testid="chatbot-continue-btn"
                    onClick={() => handleButtonClick('Bora!')}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold py-3 rounded-xl transition-colors"
                  >
                    Bora! 🔥
                  </button>
                )}

                {currentStep.type === 'buttons' && (
                  <div className="grid grid-cols-2 gap-2">
                    {currentStep.options.map((opt) => (
                      <button
                        key={opt}
                        data-testid={`chatbot-option-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => handleButtonClick(opt)}
                        className="bg-stone-700 hover:bg-amber-500 hover:text-stone-900 text-white text-xs sm:text-sm font-medium px-2 py-2.5 rounded-lg transition-colors border border-stone-600 hover:border-amber-500 break-words leading-tight min-h-[44px] flex items-center justify-center text-center"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {(currentStep.type === 'text' || currentStep.type === 'phone') && (
                  <form onSubmit={handleTextSubmit} className="flex gap-2">
                    <input
                      ref={inputRef}
                      data-testid="chatbot-text-input"
                      type={currentStep.type === 'phone' ? 'tel' : 'text'}
                      value={inputValue}
                      onChange={(e) => {
                        const v = currentStep.type === 'phone' ? formatPhone(e.target.value) : e.target.value;
                        setInputValue(v);
                        if (error) setError('');
                      }}
                      placeholder={currentStep.placeholder}
                      className="flex-1 min-w-0 bg-stone-900 border border-stone-600 rounded-xl px-4 py-2.5 text-white placeholder-stone-500 focus:border-amber-500 focus:outline-none text-base sm:text-sm"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="submit"
                      data-testid="chatbot-send-btn"
                      disabled={!inputValue.trim()}
                      className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-stone-900 px-4 rounded-xl transition-colors"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                )}

                {currentStep.type === 'finish' && (() => {
                  const message = buildWhatsAppMessage(data);
                  const waUrl = `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(message)}`;
                  return submitting ? (
                    <button
                      data-testid="chatbot-whatsapp-btn"
                      disabled
                      className="w-full bg-green-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                    >
                      <Loader2 className="w-5 h-5 animate-spin" /> Salvando teus dados...
                    </button>
                  ) : (
                    <a
                      data-testid="chatbot-whatsapp-btn"
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 no-underline"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l.226.36-1 3.648 3.74-.981.013.014z"/></svg>
                      Falar no WhatsApp agora
                    </a>
                  );
                })()}
                {currentStep.type === 'finish' && submitted && (
                  <p className="text-green-400 text-xs text-center mt-2 flex items-center justify-center gap-1">
                    ✅ Teus dados já foram registrados! Nosso comercial já recebeu.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.35s ease-out; }
      `}</style>
    </>
  );
};

export default QualifyChatbot;
